/**
 * UUIDv7 — time-ordered ids minted on the device at the moment of the tap.
 *
 * Time-ordered matters twice over: the id is the deterministic component of the
 * DynamoDB sort key, so a replayed offline sync rebuilds a byte-identical key and the
 * conditional write no-ops instead of creating a second dose; and outbox entries sort
 * naturally into the order they actually happened.
 *
 * Written by hand rather than pulled from a dependency: it is fifteen lines, and this
 * bundle is downloaded over a phone connection.
 */

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48-bit big-endian millisecond timestamp.
  const ms = BigInt(now);
  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);

  // Version 7 and the RFC 4122 variant.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const h = (i: number): string => HEX[bytes[i] ?? 0] ?? '00';
  return (
    `${h(0)}${h(1)}${h(2)}${h(3)}-${h(4)}${h(5)}-${h(6)}${h(7)}-` +
    `${h(8)}${h(9)}-${h(10)}${h(11)}${h(12)}${h(13)}${h(14)}${h(15)}`
  );
}
